import React, { useState } from "react";
import PublishIcon from "@mui/icons-material/Publish";

import { sendSentryException } from "../api/SentryErrorHandler";
import { useDataProvider, useNotify, useRecordContext, useRefresh } from "react-admin";
import { useDragAndDrop } from "../hooks/dragAndDrop";
import { Typography, Paper } from "@mui/material";

export const FileUploadComponent = ({ asset }) => {
  const dataProvider = useDataProvider();
  const record = useRecordContext();
  const [fileReader] = useState(new FileReader());
  const clientKey = record.key;
  const notify = useNotify();
  const refresh = useRefresh();

  const convertToFormEncoded = (data) => {
    return Object.entries(data)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join("&");
  };

  const handleFileUpload = (event) => {
    fileReader.onloadend = handleFileRead;
    fileReader.readAsText(event.target.files[0]);
  };

  const handleFileRead = async () => {
    let jsonData;
    let formEncodedCode;
    try {
      jsonData = JSON.parse(fileReader.result);
    } catch (e) {
      alert("Invalid JSON");
      return;
    }

    if (!("code" in jsonData)) {
      alert("Invalid JSON: 'code' not present");
      return;
    }
    if (asset !== "actions" && jsonData.definition !== undefined && typeof jsonData.definition !== "string") {
      alert("Invalid JSON: 'definition' must be of type string");
      return;
    }
    if (asset === "screens") {
      if (jsonData.definition === null) delete jsonData.definition;
      if (jsonData.definition_javascript === null) delete jsonData.definition_javascript;
    } else if (["dashboards", "menus"].includes(asset)) {
      jsonData.items = JSON.stringify(jsonData.items);
      formEncodedCode = jsonData.code;
      jsonData = JSON.stringify(jsonData);
    }
    const assetCode = formEncodedCode || jsonData.code;
    await dataProvider
      .update(asset, { id: clientKey, code: assetCode, data: jsonData })
      .then(({ data }) => {
        notify(`${asset.slice(0, -1)} (${assetCode}) uploaded/updated successfully`, { type: "success" });
        refresh();
      })
      .catch((error) => {
        sendSentryException(error, asset);
        notify(`${asset.slice(0, -1)} (${assetCode}) failed to upload. Check Sentry.`, { type: "error" });
      });
  };

  const handleFileDrop = (droppedFile) => {
    fileReader.onloadend = handleFileRead;
    fileReader.readAsText(droppedFile);
  };

  const { isDragOver, dragHandlers } = useDragAndDrop(handleFileDrop, ["json"]);

  const buttonStyle = {
    display: "inline-block",
    margin: "0 0.3em 0.3em 0",
    borderRadius: "2em",
    boxSizing: "border-box",
    textDecoration: "none",
    fontFamily: "Roboto",
    fontWeight: "300",
    color: "#FFFFFF",
    backgroundColor: "#4eb5f1",
    textAlign: "center",
    cursor: "pointer",
  };

  const uploadBtn = (
    <button style={buttonStyle}>
      <label aria-label="upload-btn" htmlFor="inputfile" style={{ cursor: "pointer" }}>
        <PublishIcon />
      </label>
    </button>
  );

  return (
    <>
      <Paper
        elevation={isDragOver ? 8 : 1}
        sx={{
          border: "2px dashed",
          borderColor: isDragOver ? "primary.main" : "grey.300",
          backgroundColor: isDragOver ? "primary.50" : "transparent",
          p: 3,
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "grey.50",
          },
        }}
        {...dragHandlers}
      >
        <Typography variant="h6" color={isDragOver ? "primary.main" : "text.secondary"}>
          {isDragOver ? "Drop your JSON file here!" : "Drag and drop your JSON file here"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Or click the button below and select a file
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Supported format: JSON
        </Typography>
        <div id="FileHandler">
          <input
            type="file"
            className="inputfile"
            id="inputfile"
            onChange={(e) => {
              handleFileUpload(e);
              e.target.value = null;
            }}
            hidden
          />
          <h5>Select file: {uploadBtn}</h5>
        </div>
      </Paper>
    </>
  );
};
